import React, { useCallback, useEffect, useState } from "react"
import { RouteComponentProps } from "react-router"
import Radium from "radium"
import { useDispatch, useSelector } from "react-redux"
import { mdiFilter, mdiFilterOutline, mdiRefresh } from "@mdi/js"

import { ColorPalette } from "../../config/colors"
import { ActionType } from "../../store/actions/actions"
import {
  ensureStringFormat,
  getDocNameMetaData,
  sortArrayOfObjects,
  toLowerCaseCustom,
  toUpperCaseCustom,
} from "../../utils"
import {
  CohortName,
  LogScreenMode,
  ProfileEventRecord,
  ProfilePk,
  UiCategoryEnum,
  EmploymentStatus,
  BaseProfileKey,
  SortDirection,
  FieldConfigKey,
  RawData,
  FieldChanges,
  ChangeLog,
  FileName,
  RecordLog,
} from "../../types"
import completedScreens from "../completedScreens"
import { ConfigService, FilterService, ProfileService, SessionService } from "../../services"
import NavBar from "../../components/Navigation/NavBar"
import SectionHeader from "../../components/Headings/SectionHeaderPrimary"
import LoadingModal from "../../components/Modals/LoadingModal"
import AlertModalOneButton from "../../components/Modals/AlertModalOneButton"
import SideMenu from "../../components/Navigation/SideMenu"
import ProfileNavMenu from "../../components/GeneralUI/ProfileNavMenu/ProfileNavMenu"
import Timeline from "../../components/GeneralUI/Timeline/Timeline"
import DataLogBlock from "../../components/Logs/DataLogBlock"
import { PeopleFlowCombinedReducer } from "../../store"
import { AssociationSettingsRepository, ProfileRepository, UsersRepository } from "../../repositories"
import { IamServiceErrorCodesEnum } from "../../enums"
import { ActionButtonType, Toolbar } from "../../components/GeneralUI/Toolbar"
import { CustomFilterState } from "../../components/Tables/DataTable/DataTable"
import Modal from "../../components/Modals/Modal"
import FieldFilter from "../../components/GeneralUI/FieldFilter/FieldFilter"
import { Profile } from "../../models"
import { FieldConfig } from "../../models/fieldConfig/fieldConfigModel"

const LOG_METADATA_KEYS = [
  "process",
  "action",
  "dateActioned",
  "userFirstName",
  "userSurname",
  "username",
  "appVersion",
  "name",
  "surname",
  "idPassport",
  "profilePk",
]
const SYSTEM_FIELD_LABELS: Record<string, string> = {
  employmentStatus: "Employment Status",
  reasonForTermination: "Reason For Termination",
  terminationDate: "Termination Date",
}
const LOGS_PER_PAGE = 15
const { GLOBAL_LOGS, INDIVIDUAL_LOGS } = LogScreenMode

const { Unauthorised } = IamServiceErrorCodesEnum
const configService = new ConfigService()
const sessionService = new SessionService()
const profileService = new ProfileService()
const filterService = new FilterService()

interface DataLogsProps extends RouteComponentProps {}

const DataLogs = (props: DataLogsProps) => {
  const [loadingModalOpen, setLoadingModalOpen] = useState(false)
  const [warningModalOpen, setWarningModalOpen] = useState(false)
  const [loadingModalMessage, setLoadingModalMessage] = useState("")
  const [loadingModalAuxMessage, setLoadingModalAuxMessage] = useState("")
  const [warningModalHeader, setWarningModalHeader] = useState("")
  const [warningModalMessage, setWarningModalMessage] = useState("")
  const [sideMenuComponents, setSideMenuComponents] = useState(<div />)
  const [sideMenuVisible, setSideMenuVisible] = useState(true)
  const [sortedEventRecords, setSortedEventRecords] = useState<ProfileEventRecord[]>([])
  const [logDataToRender, setLogData] = useState<ChangeLog[]>([])
  const [currentPageNumber, setCurrentPageNumber] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState<CustomFilterState[]>([])
  const [filterConfig, setFilterConfig] = useState<{ key: string; label: string }[]>([
    {
      key: "idPassport",
      label: "ID/Passport",
    },
  ])
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [logScreenMode, setLogScreenMode] = useState(GLOBAL_LOGS)

  const idPassport = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.idPassport)
  const password = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.password)
  const associationRepo = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.associationRepo as AssociationSettingsRepository,
  )
  const profileRepo = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.profileRepo as ProfileRepository,
  )
  const selectedAssociation = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.selectedAssociation,
  )
  const selectedCohort = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.selectedCohort)
  const selectedEmploymentStatus = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.selectedEmploymentStatus,
  )
  const currentScreen = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.currentScreen)
  const profileEntity = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.profile)
  const profilePic = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.profilePic)
  const navMenuAccess = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.navMenuAccess)
  const userRepo = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.userRepo as UsersRepository)

  const dispatch = useDispatch()
  const updateState = (data: any) => dispatch({ type: ActionType.UPDATE_STATE, data })

  useEffect(() => {
    try {
      const logScreenMode = window.location.pathname.includes("global") ? GLOBAL_LOGS : INDIVIDUAL_LOGS
      setLogScreenMode(logScreenMode)
      if (logScreenMode === GLOBAL_LOGS) {
        initialiseGlobalLogs()
      } else {
        initialiseIndividualLogs()
      }
    } catch (error) {
      closeModals()
    }
  }, [selectedAssociation, selectedCohort])

  const closeModals = () => {
    setLoadingModalOpen(false)
    setWarningModalOpen(false)
  }

  const initialiseIndividualLogs = () => {
    let currentSection = UiCategoryEnum.PEOPLE
    let currentScreen = UiCategoryEnum.LOGS

    updateState({ currentSection, currentScreen })
    const { name, surname, idPassport } = profileEntity.getPersonalInfo()
    const profilePk = profileEntity.getPk()
    if (!idPassport) {
      sessionService.logout()
      props.history.push("/login")
      return
    }
    const fieldConfigEntity = associationRepo.getFieldConfigEntity(selectedAssociation)
    initialiseSideMenu(name, surname, idPassport, profilePk)
    setFilterConfig(associationRepo.getProfileFilterConfig(selectedAssociation))
    initialiseLogs(fieldConfigEntity, profileRepo, { logScreenMode: INDIVIDUAL_LOGS, profileEntity })
  }

  const initialiseGlobalLogs = () => {
    const currentSection = UiCategoryEnum.GLOBAL_LOGS
    const currentScreen = UiCategoryEnum.DATA_LOGS
    const fieldConfigEntity = associationRepo.getFieldConfigEntity(selectedAssociation)

    updateState({ currentSection, currentScreen })
    setFilterConfig(associationRepo.getProfileFilterConfig(selectedAssociation))
    initialiseLogs(fieldConfigEntity, profileRepo, { logScreenMode: GLOBAL_LOGS, targetCohorts: [selectedCohort] })
  }

  const initialiseLogs = (
    fieldConfigEntity: FieldConfig,
    profileRepo: ProfileRepository,
    refreshArgs:
      | { logScreenMode: LogScreenMode.INDIVIDUAL_LOGS; profileEntity: Profile }
      | { logScreenMode: LogScreenMode.GLOBAL_LOGS; targetCohorts: CohortName[] },
  ) => {
    const keepOnlyChangedRawData = (records: ProfileEventRecord[]) => {
      return records.map((record) => {
        const previousRawData = (record.previousRawData as RawData) || {}
        const rawData = (record.rawData as RawData) || {}
        const validKeys = [...Object.keys(previousRawData), ...LOG_METADATA_KEYS]
        let newRawData = {} as RawData
        validKeys.forEach((key) => {
          newRawData[key] = rawData[key]
        })
        record.rawData = newRawData
        return record
      })
    }
    const mergeRecords = (recordArray: Record<string, any>[]) => {
      let newRecord = {
        rawData: {},
        previousRawData: {},
        docs: [] as FileName[],
        photos: [] as FileName[],
        logs: [] as RecordLog[],
      }
      recordArray.forEach((record) => {
        if (record.rawData) {
          newRecord.rawData = { ...newRecord.rawData, ...record.rawData }
        }
        if (record.previousRawData) {
          newRecord.previousRawData = { ...newRecord.previousRawData, ...record.previousRawData }
        }
        if (record.docs) {
          newRecord.docs = [...newRecord.docs, ...record.docs]
        }
        if (record.photos) {
          newRecord.photos = [...newRecord.photos, ...record.photos]
        }
        if (record.logs) {
          newRecord.logs = [...newRecord.logs, ...record.logs]
        }
      })
      return newRecord as unknown as ProfileEventRecord
    }
    const mergeRecordsWithSameDateAction = (records: ProfileEventRecord[]) => {
      const recordsByDateAction = {} as Record<string, ProfileEventRecord>
      const recordsNoDateActioned = [] as ProfileEventRecord[]
      records.forEach((record) => {
        const dateActioned = record.rawData.dateActioned
        if (!dateActioned) {
          recordsNoDateActioned.push(record)
          return
        }
        if (recordsByDateAction[dateActioned]) {
          recordsByDateAction[dateActioned] = mergeRecords([recordsByDateAction[dateActioned], record])
        } else {
          recordsByDateAction[dateActioned] = record
        }
      })
      return [...Object.values(recordsByDateAction), ...recordsNoDateActioned]
    }

    const getEventRecords = (): ProfileEventRecord[] => {
      const { logScreenMode } = refreshArgs
      let targetProfileEntities: Record<ProfilePk, Profile> = {}
      if (logScreenMode === GLOBAL_LOGS) {
        targetProfileEntities = profileRepo.getTargetProfiles(refreshArgs.targetCohorts)
      } else {
        const pk = refreshArgs.profileEntity.getPk()
        targetProfileEntities = { [pk]: refreshArgs.profileEntity }
      }
      return Object.values(targetProfileEntities).flatMap((profileEntity) =>
        profileEntity.getAllProfileRecordsLabelled(selectedAssociation),
      )
    }

    const sortEventRecords = (records: ProfileEventRecord[]): ProfileEventRecord[] => {
      let recordsNoDateActioned = [] as ProfileEventRecord[]
      let recordsWithDateActioned = [] as ProfileEventRecord[]
      records.forEach((record) => {
        if (!record.rawData.dateActioned) {
          recordsNoDateActioned.push(record)
        } else {
          recordsWithDateActioned.push(record)
        }
      })
      const sortedRecordsWithDateActioned = sortArrayOfObjects(
        "dateActioned",
        SortDirection.DESCENDING,
        recordsWithDateActioned,
        "rawData",
      )
      return keepOnlyChangedRawData([...sortedRecordsWithDateActioned, ...recordsNoDateActioned])
    }

    setLoadingModalOpen(true)
    setLoadingModalMessage("Refreshing change logs")
    setTimeout(() => {
      const unsortedEventRecords = getEventRecords()
      const mergedRecords = mergeRecordsWithSameDateAction(unsortedEventRecords)
      const sortedEventRecords = sortEventRecords(mergedRecords)
      if (sortedEventRecords) {
        const validSortedRecords = sortedEventRecords.filter(
          (record) =>
            (record.previousRawData && Object.keys(record.previousRawData).length) ||
            (record.docs && record.docs.length),
        )
        const numberOfPages = getTotalNumberOfPages(validSortedRecords.length, LOGS_PER_PAGE)
        const logData = validSortedRecords.map((record) => eventRecordToLog(fieldConfigEntity, record))
        setSortedEventRecords(validSortedRecords)
        setLogData(logData)
        setTotalPages(numberOfPages)
      }
      closeModals()
    }, 1000)
  }

  const eventRecordToLog = (fieldConfigEntity: FieldConfig, record: ProfileEventRecord): ChangeLog => {
    const { previousRawData = {}, docs = [], logs = [] } = record
    const rawData = record.rawData as RawData
    const { userFirstName, userSurname, appVersion, name, surname, idPassport, dateActioned, profilePk } = rawData
    let fieldChanges = {} as FieldChanges
    console.log("[Logs] previousRawData keys:", Object.keys(previousRawData))
    Object.keys(previousRawData).forEach((key) => {
      if (LOG_METADATA_KEYS.includes(key)) {
        return
      }
      const oldValue = ensureStringFormat(previousRawData[key]) || " ----- "
      const newValue = ensureStringFormat(rawData[key]) || " ----- "
      const fieldConfigItem = fieldConfigEntity.getFieldConfigItemEntity(key as FieldConfigKey)
      // let label = toUpperCaseCustom(key)
      if (!fieldConfigItem && !SYSTEM_FIELD_LABELS[key]) {
        console.warn(`[Logs] Field "${key}" not in fieldConfig — skipping`)
        return
      }
      const label = toUpperCaseCustom(fieldConfigItem ? fieldConfigItem.getLabel() : SYSTEM_FIELD_LABELS[key])
      fieldChanges[label] = { old: oldValue, new: newValue }
    })

    const docCreationData = docs?.map((fileNameAndFormat) => ({
      ...getDocNameMetaData(fileNameAndFormat),
    }))
    const docDeletionData = logs
      .filter((log) => log.op === "remove" && log.path.startsWith("/docs/"))
      .map((log) => {
        const docFileName = log.path.replace("/docs/", "")
        return { ...getDocNameMetaData(docFileName), op: "remove", unixMs: log.unixMs }
      })
    const docsMetaData = [...docCreationData, ...docDeletionData]
    const logMetaData = {
      userFirstName,
      userSurname,
      appVersion: `${appVersion}`,
      name,
      surname,
      idPassport,
      dateActioned,
      profilePk,
    }
    return {
      docsMetaData,
      fieldChanges,
      logMetaData,
    }
  }

  const getTotalNumberOfPages = (totalNumberOfRecords: number, pageSize: number) => {
    return Math.ceil(totalNumberOfRecords / pageSize)
  }

  const initialiseSideMenu = (name: string, surname: string, idPassport: string, profilePk: ProfilePk) => {
    const sideMenuHandler = (screen: string) => {
      if (completedScreens.includes(screen)) {
        updateState({ currentScreen: screen })
        props.history.push(`/people/${toLowerCaseCustom(screen.split(" ").join(""))}`)
      } else {
        props.history.push("/comingsoon")
      }
    }

    const iamEntity = userRepo.getCurrentUserEntity().getAssocationIamEntity(selectedAssociation)
    if (!iamEntity) {
      throw { code: Unauthorised }
    }
    const profileNavMenuAccess = configService.generateProfileNavMenuConfig(
      iamEntity,
      selectedEmploymentStatus as EmploymentStatus,
    )
    const sideMenuComponents = (
      <ProfileNavMenu
        profilePic={profilePic}
        name={`${name} ${surname}`}
        idPassport={idPassport}
        profilePk={profilePk}
        onClick={(screen: string) => sideMenuHandler(screen)}
        currentScreen={currentScreen}
        accessFlags={profileNavMenuAccess}
      />
    )
    updateState({ profileNavMenuAccess })
    setSideMenuComponents(sideMenuComponents)
  }

  const renderLogComponents = useCallback(() => {
    if (!logDataToRender.length) {
      return []
    }
    const startIndex = (currentPageNumber - 1) * LOGS_PER_PAGE
    const endIndex = startIndex + LOGS_PER_PAGE
    const logsOnPage = logDataToRender.slice(startIndex, endIndex)
    return logsOnPage.map((log, index) => buildLogComponent(log, index, logScreenMode))
  }, [sortedEventRecords.length, logDataToRender, currentPageNumber, logScreenMode])

  const buildLogComponent = (logData: ChangeLog, index: number, logScreenMode: LogScreenMode) => {
    return <DataLogBlock mode={logScreenMode} logData={logData} key={`log_${index}`} />
  }

  const loadMoreLogs = async (direction: string) => {
    const onFirstPage = currentPageNumber === 1
    if (onFirstPage && direction === "previous") {
      return
    }
    const onLastPage = currentPageNumber === totalPages
    if (onLastPage && direction === "next") {
      return
    }
    setLoadingModalOpen(true)
    setLoadingModalMessage("Loading more logs...")
    const newPageNumber = currentPageNumber + (direction === "next" ? 1 : -1)
    setCurrentPageNumber(newPageNumber)
    closeModals()
  }

  const updateLogsRenderData = (
    eventRecords: ProfileEventRecord[],
    filters: CustomFilterState[],
    fieldConfigEntity: FieldConfig,
  ) => {
    const targetDataPath = ["rawData"]
    const filteredEventRecords = filterService.applyCustomFilters(eventRecords, filters, targetDataPath)
    const allLogData = filteredEventRecords.map((record) => eventRecordToLog(fieldConfigEntity, record))
    setLogData(allLogData)
    setCurrentPageNumber(1)
    setTotalPages(getTotalNumberOfPages(eventRecords.length, LOGS_PER_PAGE))
  }

  const goToProfiles = () => {
    closeModals()
    window.scrollTo(0, 0)
    const prevRouteState = props.location.state || {}
    setTimeout(() => props.history.push("/people", prevRouteState), 30)
  }

  const refreshProfileData = async () => {
    try {
      const profilePk = profileEntity.getPk()
      const authCredentials = { username: idPassport, password }
      const fieldConfigEntity = associationRepo.getFieldConfigEntity(selectedAssociation)
      const loadingMessage = logScreenMode === INDIVIDUAL_LOGS ? "Downloading profile..." : "Downloading profiles..."
      setLoadingModalMessage(loadingMessage)
      setLoadingModalOpen(true)

      if (logScreenMode === INDIVIDUAL_LOGS) {
        await profileService.refreshProfile(selectedAssociation, profileRepo, authCredentials, profilePk)
        setTimeout(
          () => initialiseLogs(fieldConfigEntity, profileRepo, { logScreenMode: INDIVIDUAL_LOGS, profileEntity }),
          250,
        )
      } else {
        await profileService.downloadAssociationProfiles(
          false,
          sessionService,
          associationRepo,
          profileRepo,
          authCredentials,
          selectedAssociation,
          selectedCohort,
          (progress: string) => {
            setLoadingModalAuxMessage(progress)
          },
        )
        setTimeout(
          () =>
            initialiseLogs(fieldConfigEntity, profileRepo, {
              logScreenMode: GLOBAL_LOGS,
              targetCohorts: [selectedCohort],
            }),
          250,
        )
      }
      setLoadingModalAuxMessage("")
    } catch (error: any) {
      console.error("refreshProfileData error", error)
    }
    closeModals()
  }

  const toolbarActionButtons: ActionButtonType[] = [
    {
      iconPath: filters.length > 0 ? mdiFilter : mdiFilterOutline,
      iconColor: ColorPalette.PRIMARY_BLUE,
      onClick: () => setFilterModalOpen(true),
      label: "FILTER",
      title: "Set filters",
      count: filters.length,
    },
    {
      iconPath: mdiRefresh,
      iconColor: ColorPalette.PRIMARY_BLUE,
      onClick: refreshProfileData,
      label: "REFRESH",
      title: "Check for updates",
    },
  ]

  let loadingModal = null
  if (loadingModalOpen) {
    loadingModal = (
      <LoadingModal open={true} auxilliaryMessage={loadingModalAuxMessage}>
        {loadingModalMessage}
      </LoadingModal>
    )
  }

  const logComponents = renderLogComponents()
  const { hasDataLogsAccess } = navMenuAccess
  const shouldPaginate =
    (logDataToRender && logDataToRender.length > LOGS_PER_PAGE) || sortedEventRecords.length > LOGS_PER_PAGE
  const key = `${sortedEventRecords.length}_${logDataToRender.length}`
  const fieldConfigEntity = associationRepo.getFieldConfigEntity(selectedAssociation)

  let sideMenu = null
  if (logScreenMode === INDIVIDUAL_LOGS) {
    sideMenu = <SideMenu visible={sideMenuVisible} menuComponents={sideMenuComponents} />
  }

  return (
    <div style={styles.container}>
      <NavBar match={props.match} location={props.location} history={props.history} />

      <SectionHeader style={styles.sectionHeader} disabled={true} onClick={() => setSideMenuVisible(false)}>
        {currentScreen}
      </SectionHeader>

      <div style={styles.contentContainer}>
        {sideMenu}
        <div style={{ ...styles.rightSide, paddingInline: sideMenuVisible ? "max(2em, 2%)" : "min(3em, 3%)" }}>
          <Toolbar
            actionButtons={toolbarActionButtons}
            navButtons={{ left: { label: "BACK", onClick: goToProfiles } }}
            key={`actionToolbar_${toolbarActionButtons.length}`}
          />
          <div style={styles.rightSideContent}>
            <Timeline
              logs={logComponents}
              shouldPaginate={shouldPaginate}
              isFirstPage={currentPageNumber === 1}
              isLastPage={currentPageNumber === totalPages}
              loadPrevious={() => loadMoreLogs("previous")}
              loadNext={() => loadMoreLogs("next")}
              containerStyle={{ flex: 1 }}
              key={`timelineLogs_${key}_${currentPageNumber}_${totalPages}`}
            />
          </div>
        </div>
      </div>
      {loadingModal}
      <AlertModalOneButton
        open={warningModalOpen}
        header={warningModalHeader}
        body={warningModalMessage}
        buttonLabel={"Ok"}
        onClick={closeModals}
      />
      <AlertModalOneButton
        open={!hasDataLogsAccess}
        header={"Not Authorised"}
        body={"You don't have permission to view profile logs."}
        buttonLabel={"Ok"}
        opaqueBackground={true}
        onClick={() => props.history.goBack()}
      />
      <Modal open={filterModalOpen} title={""} contentContainerStyle={{ padding: 0 }}>
        <FieldFilter
          filtersOnLoad={filters}
          filterConfig={filterConfig}
          onDoneClick={(updatedFilters) => {
            setFilters(updatedFilters)
            updateLogsRenderData(sortedEventRecords, updatedFilters, fieldConfigEntity)
            setFilterModalOpen(false)
          }}
        />
      </Modal>
    </div>
  )
}

const styles: Record<string, React.CSSProperties | Record<string, React.CSSProperties>> = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    backgroundImage: `linear-gradient(to bottom, ${ColorPalette.SCREEN_TOP_GRADIENT}, ${ColorPalette.SCREEN_BOTTOM_GRADIENT})`,
    height: "100vh",
  },
  sectionHeader: {
    margin: "3.5% auto 1.5%",
  },
  contentContainer: {
    display: "flex",
    flex: 1,
    overflow: "auto",
  },
  rightSide: {
    display: "flex",
    flexDirection: "column",
    paddingInline: "max(2em, 2%)",
    overflow: "hidden",
    flex: 1,
  },
  rightSideContent: {
    boxShadow: "0px -1px 8px rgba(60,60,60, 0.1)",
    display: "flex",
    flex: 1,
    backgroundColor: ColorPalette.CARD_WHITE,
  },
  buttonIconStyle: {
    color: ColorPalette.PRIMARY_TEXT,
    width: "1.2rem",
    height: "1.2rem",
  },
  backButton: {
    fontWeight: "550",
    fontSize: "0.8rem",
    color: ColorPalette.SECONDARY_TEXT,
    height: 40,
    ":hover": {
      color: ColorPalette.PRIMARY_BLUE,
    },
    ":active": {
      color: ColorPalette.DARK_GREY,
    },
  },
}

export default Radium(DataLogs)
